import { useParams, useNavigate } from "react-router-dom";
import { 
  useV1ProductionProductionOrdersRetrieve,
  useV1ProductionWorkOrdersList,
  v1ProductionProductionOrdersStartCreate,
  v1ProductionProductionOrdersCompleteCreate,
  v1ProductionProductionOrdersCancelCreate,
  v1ProductionProductionOrdersHoldCreate,
  v1ProductionProductionOrdersResumeCreate,
  v1ProductionProductionOrdersReleaseCreate,
  v1ProductionWorkOrdersStartCreate,
  v1ProductionWorkOrdersCompleteCreate,
  v1ProductionWorkOrdersCancelCreate,
  v1ProductionWorkOrdersPauseCreate,
  v1ProductionWorkOrdersResumeCreate
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { PageBanner } from "@/shared/components/PageBanner";
import { HoldProductionOrderDialog } from "@/features/production-orders/components/HoldProductionOrderDialog";
import { PauseWorkOrderDialog } from "@/features/production-orders/components/PauseWorkOrderDialog";

export function ProductionOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = usePermission("production.manage");
  
  const { data: order, isLoading, isError } = useV1ProductionProductionOrdersRetrieve(id!);
  const { data: workOrdersData } = useV1ProductionWorkOrdersList({ production_order: id });

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isHoldOrderModalOpen, setIsHoldOrderModalOpen] = useState(false);
  const [pausingWorkOrder, setPausingWorkOrder] = useState<{ id: string; number?: string } | null>(null);

  if (isLoading) return <div style={{ padding: "2rem", textAlign: "center" }}>در حال بارگذاری...</div>;
  if (isError || !order) return <div style={{ padding: "2rem", color: "#EF4444", textAlign: "center" }}>خطا در دریافت اطلاعات سفارش</div>;

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ["v1ProductionProductionOrdersRetrieve", id] });
    queryClient.invalidateQueries({ queryKey: ["v1ProductionWorkOrdersList"] });
  };

  const handleOrderAction = async (actionFn: any, payload?: any) => {
    if (!canManage) return;
    try {
      setIsActionLoading(true);
      await actionFn(id!, payload || ({} as any));
      refreshData();
    } catch (err) {
      alert("خطا در انجام عملیات");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleWorkOrderAction = async (woId: string, actionFn: any, payload?: any) => {
    if (!canManage) return;
    try {
      setIsActionLoading(true);
      await actionFn(woId, payload || ({} as any));
      refreshData();
    } catch (err) {
      alert("خطا در انجام عملیات");
    } finally {
      setIsActionLoading(false);
    }
  };

  const renderOrderActions = () => {
    if (!canManage) return null;
    const s = order.status;
    return (
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {s === 'draft' && <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#ffffff", color: "#007270", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => handleOrderAction(v1ProductionProductionOrdersReleaseCreate)} disabled={isActionLoading}>آزادسازی (Release)</button>}
        {s === 'released' && <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#ffffff", color: "#007270", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => handleOrderAction(v1ProductionProductionOrdersStartCreate)} disabled={isActionLoading}>شروع (Start)</button>}
        {s === 'in_progress' && <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#10B981", color: "#ffffff", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => handleOrderAction(v1ProductionProductionOrdersCompleteCreate)} disabled={isActionLoading}>تکمیل (Complete)</button>}
        {s === 'in_progress' && <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#F59E0B", color: "#ffffff", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => setIsHoldOrderModalOpen(true)} disabled={isActionLoading}>توقف (Hold)</button>}
        {s === 'on_hold' && <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#ffffff", color: "#007270", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => handleOrderAction(v1ProductionProductionOrdersResumeCreate)} disabled={isActionLoading}>ادامه (Resume)</button>}
        {['draft', 'released', 'in_progress', 'on_hold'].includes(s || '') && (
          <button style={{ padding: "0.5rem 1rem", borderRadius: "10px", background: "#EF4444", color: "#ffffff", fontWeight: 700, border: "none", cursor: "pointer" }} onClick={() => handleOrderAction(v1ProductionProductionOrdersCancelCreate)} disabled={isActionLoading}>لغو (Cancel)</button>
        )}
      </div>
    );
  };

  const renderWorkOrderActions = (wo: any) => {
    if (!canManage) return null;
    const s = wo.status;
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        {s === 'released' && <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#007270", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => handleWorkOrderAction(wo.id, v1ProductionWorkOrdersStartCreate)} disabled={isActionLoading}>Start</button>}
        {s === 'started' && <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#10B981", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => {
          const qty = prompt("تعداد تکمیل‌شده:");
          if (qty) handleWorkOrderAction(wo.id, v1ProductionWorkOrdersCompleteCreate, { completed_quantity: Number(qty) });
        }} disabled={isActionLoading}>Complete</button>}
        {s === 'started' && <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#F59E0B", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => setPausingWorkOrder({ id: wo.id, number: wo.work_order_number || wo.id })} disabled={isActionLoading}>Pause</button>}
        {s === 'paused' && <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#007270", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => handleWorkOrderAction(wo.id, v1ProductionWorkOrdersResumeCreate)} disabled={isActionLoading}>Resume</button>}
        {['started', 'paused'].includes(s || '') && (
          <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#3B82F6", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => navigate("/production/consumption")} disabled={isActionLoading}>مصرف متریال</button>
        )}
        {['released', 'started', 'paused'].includes(s || '') && (
          <button style={{ padding: "4px 8px", borderRadius: "6px", background: "#EF4444", color: "#ffffff", fontSize: "0.75rem", border: "none", cursor: "pointer" }} onClick={() => handleWorkOrderAction(wo.id, v1ProductionWorkOrdersCancelCreate)} disabled={isActionLoading}>Cancel</button>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0" }}>
      <PageBanner
        title={`جزئیات سفارش: ${order.order_number}`}
        subtitle="مشاهده وضعیت، کارهای اجرایی (Work Orders)، و مدیریت چرخه حیات سفارش تولید"
        icon="📋"
       helpLink="/help/production/production-order-detail" >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/production/orders")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← برگشت
          </button>
          {renderOrderActions()}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
          <div><strong style={{ color: "#666" }}>وضعیت:</strong> <span style={{ fontWeight: 700, color: "#007270" }}>{order.status}</span></div>
          <div><strong style={{ color: "#666" }}>اولویت:</strong> <span style={{ fontWeight: 700 }}>{order.priority}</span></div>
          <div><strong style={{ color: "#666" }}>تاریخ آزادسازی:</strong> {order.released_at ? new Date(order.released_at).toLocaleDateString("fa-IR") : "—"}</div>
          <div><strong style={{ color: "#666" }}>موعد تحویل:</strong> {order.due_date ? new Date(order.due_date).toLocaleDateString("fa-IR") : "—"}</div>
          <div><strong style={{ color: "#666" }}>تعداد برنامه‌ریزی‌شده:</strong> <span style={{ fontWeight: 700 }}>{order.planned_quantity}</span></div>
        </div>

        <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem" }}>
          <CustomFieldsPanel
            targetContentType="production.productionorder"
            targetObjectId={id}
          />
        </div>
      </div>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#007270", marginBottom: "1rem" }}>
          کارهای اجرایی مرتبط (Work Orders)
        </h2>

        {workOrdersData?.results && workOrdersData.results.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)", color: "#007270" }}>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>کد کار (WO)</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>مرحله/عملیات</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>ایستگاه کاری</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>تعداد تکمیلی</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>وضعیت</th>
                  <th style={{ padding: "0.75rem 1rem", fontWeight: "700" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {workOrdersData.results.map((wo: any) => (
                  <tr key={wo.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "0.75rem 1rem", fontWeight: "600" }}>{wo.work_order_number}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>{wo.sequence_no}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>{wo.work_center_display?.name || wo.work_center || "-"}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>{wo.completed_quantity} / {order.planned_quantity}</td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <span style={{ padding: "0.25rem 0.5rem", borderRadius: "6px", fontSize: "0.8rem", fontWeight: 700, background: "rgba(0, 114, 112, 0.1)", color: "#007270" }}>
                        {wo.status}
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>{renderWorkOrderActions(wo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: "#666", fontSize: "0.85rem", margin: 0 }}>هیچ کار اجرایی بر اساس Routing برای این سفارش ثبت نشده است.</p>
        )}
      </div>

      <HoldProductionOrderDialog
        productionOrderId={id!}
        open={isHoldOrderModalOpen}
        onClose={() => {
          setIsHoldOrderModalOpen(false);
          refreshData();
        }}
      />

      {pausingWorkOrder && (
        <PauseWorkOrderDialog
          workOrderId={pausingWorkOrder.id}
          workOrderNumber={pausingWorkOrder.number}
          open={Boolean(pausingWorkOrder)}
          onClose={() => setPausingWorkOrder(null)}
          onSuccess={refreshData}
        />
      )}
    </div>
  );
}
